import { Button, Icon, Input, Label } from '@components';
import { NAME_MAX_LENGTH, NAME_MIN_LENGTH } from '@constants/generic';
import { ifscCodeLength } from '@constants/upi';
import { FieldError } from '@types';

import { VerifiedBankInfoType } from '../types';

interface BankAccountDetailsProps {
  info: any;
  onInputChange: (field: string, value: string | number) => void;
  isLoadingAccountDetails: boolean;
  fieldError: FieldError;
  setFieldError: (fieldError: FieldError) => void;
  isVerified?: boolean;
  verifiedName?: VerifiedBankInfoType;
  onEdit?: () => void;
  onVerify?: () => void;
  onCancel?: () => void;
  isVerifyLoading?: boolean;
  isVerifyDisabled?: boolean;
  isEditing?: boolean;
}

const BankAccountDetails = ({
  info,
  onInputChange,
  isLoadingAccountDetails,
  fieldError,
  setFieldError,
  isVerified,
  verifiedName,
  onEdit,
  onVerify,
  onCancel,
  isVerifyLoading,
  isVerifyDisabled,
  isEditing
}: BankAccountDetailsProps) => {
  const clearError = () => {
    setFieldError({
      field: null,
      message: ''
    });
  };

  const handleVerify = () => {
    clearError();
    onVerify?.();
  };

  const onAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearError();
    onInputChange('accountNumber', e.target.value);
  };

  const onConfirmAccountNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    clearError();
    onInputChange('confirmAccountNumber', e.target.value);
  };

  const onIfscChange = (value: string) => {
    clearError();
    onInputChange('ifsc', value.toUpperCase());
  };

  return (
    <div className="w-full">
      <div className="mt-6 flex flex-col gap-6">
        <div>
          <Label label="Account number" isRequired className="!mb-1" />
          <Input
            name="accountNumber"
            value={info?.accountNumber}
            placeholder="Enter your account number"
            onChange={onAccountNumberChange}
            className="rounded-xl"
            maxLength={NAME_MAX_LENGTH}
            minLength={NAME_MIN_LENGTH}
            error={fieldError.field === 'accountNumber'}
            errorMessage={fieldError.message}
            isLoading={isLoadingAccountDetails}
            disabled={isVerified}
            inputStyle={
              isVerified
                ? '!bg-cofeeGreyBlue75 !border-none text-cofeeNeutral7'
                : ''
            }
          />
        </div>
        <div>
          <Label label="Confirm account number" isRequired className="!mb-1" />
          <Input
            name="confirmAccountNumber"
            value={info?.confirmAccountNumber}
            placeholder="Confirm account number"
            onChange={onConfirmAccountNumberChange}
            className="rounded-xl"
            maxLength={NAME_MAX_LENGTH}
            minLength={NAME_MIN_LENGTH}
            error={fieldError.field === 'accountNumber'}
            errorMessage={fieldError.message}
            disabled={isVerified}
            inputStyle={
              isVerified
                ? '!bg-cofeeGreyBlue75 !border-none text-cofeeNeutral7'
                : ''
            }
            isLoading={isLoadingAccountDetails}
          />
        </div>
        <div>
          <Label label="IFSC code" isRequired className="!mb-1" />
          <Input
            name="ifsc"
            value={info?.ifsc}
            placeholder="Enter IFSC code"
            handleInputChange={onIfscChange}
            className="rounded-xl"
            maxLength={ifscCodeLength}
            minLength={3}
            error={fieldError.field === 'ifsc'}
            errorMessage={fieldError.message}
            disabled={isVerified}
            inputStyle={
              isVerified
                ? '!bg-cofeeGreyBlue75 !border-none text-cofeeNeutral7'
                : ''
            }
            isLoading={isLoadingAccountDetails}
          />
        </div>
      </div>
      {fieldError.field === 'verify' && (
        <div className="absolute text-xs text-error">{fieldError.message}</div>
      )}
      {verifiedName?.userName ? (
        <>
          <div
            className={`flex items-start gap-1 pb-6 ${
              fieldError.field === 'verify' ? 'mt-4' : 'mt-1'
            }`}
          >
            <Icon name="stepper-completed" size="16" className="mt-1" />
            <div className="flex flex-col">
              <span className="text-cofeeGreen8">{verifiedName.userName}</span>
              {/* Already verified: bank name from kyc-details (bankAddress/bankBranchName).
              New onboarding: from verify API (bankName = bank_address). */}
              {(verifiedName.bankAddress ||
                verifiedName.bankName ||
                verifiedName.bankBranchName) && (
                <span className="text-cofeeGreen8">
                  {verifiedName.bankAddress ??
                    verifiedName.bankName ??
                    verifiedName.bankBranchName}
                </span>
              )}
            </div>
          </div>
          {onEdit && (
            <Button
              variant="tertiary"
              label="Edit"
              leftIconName="edit-pencil-greyBlue"
              handleClick={onEdit}
            />
          )}
        </>
      ) : (
        onVerify && (
          <div className="flex items-center gap-3">
            {isEditing && onCancel && (
              <Button
                variant="tertiary"
                label="Cancel"
                className="mt-6"
                handleClick={onCancel}
              />
            )}
            <Button
              variant="primary"
              label="Verify account"
              className="mt-6"
              handleClick={handleVerify}
              loading={isVerifyLoading}
              disabled={isVerifyDisabled || isVerified}
            />
          </div>
        )
      )}
    </div>
  );
};

export default BankAccountDetails;
